import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { urgentCaseAPI } from "../../services/api";
import "../../pages/reception/EmergencyQueue.css";
import EmergencyAcceptedModal from "./EmergencyAcceptedModal";
import { useTranslation } from "react-i18next";

const EmergencyQueue = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language === "ar" ? "ar-EG" : "en-US";
  const noData = t("emergencyQueue.common.noData");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cases, setCases] = useState([]);
  const [acceptedCase, setAcceptedCase] = useState(null);
  const [activeCase, setActiveCase] = useState(null);

  const fetchQueue = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError("");
      const res = await urgentCaseAPI.getAcceptableCases();
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : data?.data || [];
      setCases(list);
    } catch (e) {
      setError(e?.message || t("emergencyQueue.errors.load"));
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchActiveCase = async () => {
    try {
      const res = await urgentCaseAPI.getActiveUrgentCasesForVeterinarian();
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : data?.data || [];
      setActiveCase(Array.isArray(list) && list.length > 0 ? list[0] : null);
    } catch (_) { }
  };

  useEffect(() => {
    const load = async (isInitial = false) => {
      await fetchQueue(isInitial);
      await fetchActiveCase();
    };

    // Initial load with loading spinner
    load(true);

    // Background refresh without loading spinner
    const interval = setInterval(() => {
      load(false);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const formatRemainingTime = (seconds) => {
    if (!seconds || seconds <= 0) return "Expired";
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${secs}s remaining`;
    }
    return `${secs}s remaining`;
  };

  const handleViewActiveCase = () => {
    if (activeCase) {
      setAcceptedCase(activeCase);
    }
  };

  const handleAccept = async (id) => {
    if (activeCase) {
      alert(t("emergencyQueue.alerts.hasActiveCase"));
      return;
    }
    try {
      const res = await urgentCaseAPI.acceptCase(id);
      const accepted = res?.data?.data || res?.data?.case || res?.data || null;
      setCases((prev) => prev.filter((c) => String(c.id) !== String(id)));
      if (accepted) {
        setAcceptedCase(accepted);
        // Store the case ID for later use in appointment modal
        const caseId = accepted.id || id;
        if (caseId) {
          localStorage.setItem('urgentCaseId', String(caseId));
        }
      }
      setActiveCase(accepted);
    } catch (e) {
      setCases((prev) => prev.filter((c) => String(c.id) !== String(id)));
    }
  };

  return (
    <div
      className="reception-main-section"
      dir={i18n.dir()}
      lang={i18n.language}
    >
      <div className="reception-section-header eq-header">
        <h2 className="services-title">{t("emergencyQueue.title")}</h2>
        <button
          type="button"
          className="eq-refresh-btn"
          onClick={() => {
            fetchQueue(false);
            fetchActiveCase();
          }}
          aria-label={t("emergencyQueue.refresh")}
        >
          <span>⟳</span>
          {t("emergencyQueue.refresh")}
        </button>
      </div>
      {activeCase && (
        <div className="eq-active-banner">
          <span className="icon">⚠️</span>
          <span>
            {t("emergencyQueue.activeBanner")} - #{activeCase.case_ref || activeCase.id}
          </span>
        </div>
      )}
      <div className="reception-filters-row"></div>
      <div className="services-table-wrapper">
        {loading ? (
          <div className="loading-spinner"></div>
        ) : error ? (
          <div className="reception-error-state">{error}</div>
        ) : (
          <table className="services-table">
            <thead>
              <tr>
                <th>{t("emergencyQueue.table.caseRef")}</th>
                <th>{t("emergencyQueue.table.created")}</th>
                <th>{t("emergencyQueue.table.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {activeCase && (
                <tr className="eq-active-case-row">
                  <td>
                    <div className="eq-case-ref">
                      <span className={`eq-status-badge ${activeCase.status === "accepted" ? "accepted" : "pending"}`}>
                        {activeCase.status || "active"}
                      </span>
                      #{activeCase.case_ref || activeCase.id}
                    </div>
                  </td>

                  <td>
                    <div className="eq-time-info">
                      {new Date(
                        activeCase.created_at || activeCase.createdAt || Date.now()
                      ).toLocaleString(locale)}
                      {activeCase.remaining_time_in_seconds && (
                        <div className={`eq-remaining-time ${activeCase.remaining_time_in_seconds < 300 ? "danger" : "warning"}`}>
                          {formatRemainingTime(activeCase.remaining_time_in_seconds)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <button
                      className="manage-team-add-btn eq-view-btn"
                      onClick={handleViewActiveCase}
                    >
                      {t("emergencyQueue.buttons.viewDetails") || "View Details"}
                    </button>
                  </td>
                </tr>
              )}
              {cases.length === 0 && !activeCase ? (
                <tr>
                  <td colSpan={4} className="eq-empty">
                    {t("emergencyQueue.empty")}
                  </td>
                </tr>
              ) : (
                cases.map((c) => (
                  <tr key={c.id}>
                    <td>#{c.case_ref || c.id}</td>

                    <td>
                      {new Date(
                        c.created_at || c.createdAt || Date.now()
                      ).toLocaleString(locale)}
                    </td>
                    <td>
                      <button
                        className="manage-team-add-btn eq-accept-btn"
                        disabled={!!activeCase}
                        onClick={() => handleAccept(c.id)}
                      >
                        {t("emergencyQueue.buttons.accept")}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {acceptedCase && (
        <EmergencyAcceptedModal caseData={acceptedCase} onClose={() => setAcceptedCase(null)} />
      )}
    </div>
  );
};

export default EmergencyQueue;


